"use client";

import {
  Camera,
  Clock3,
  MapPin,
  ShoppingBag
} from "lucide-react";
import { motion } from "motion/react";
import Image from "next/image";

const perks = [
  {
    icon: <Camera className="h-5 w-5" />,
    title: "Share dishes people follow",
    description: "Post the plates, prep, and recipes that make your cooking unmistakably yours.",
  },
  {
    icon: <ShoppingBag className="h-5 w-5" />,
    title: "Add orders later",
    description: "Start with visibility first, then switch on menus and orders when you are ready.",
  },
  {
    icon: <Clock3 className="h-5 w-5" />,
    title: "Create on your schedule",
    description: "Show up when it fits your life, your routine, and the way you actually cook.",
  },
  {
    icon: <MapPin className="h-5 w-5" />,
    title: "Need a kitchen?",
    description: "Find space to cook, or open your kitchen to other creators nearby.",
  },
];

export function FoodCreatorPerks() {
  return (
    <section
      className="bg-[linear-gradient(135deg,rgba(255,95,71,0.08)_0%,rgba(255,241,237,0.92)_48%,rgba(10,92,54,0.06)_100%)] py-20 lg:py-24"
      id="why-creators"
      data-section-theme="light"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="order-2 lg:order-1"
          >
            <h2 className="font-asgard text-4xl leading-[0.96] tracking-[-0.04em] text-[#02120A] sm:text-5xl">
              Start with your food story. Sell when it makes sense.
            </h2>
            <p className="mb-6 mt-5 font-satoshi text-lg leading-8 text-[#02120A]/72 sm:mb-8">
              Build trust around what you cook, let people discover your style, and grow into orders on your own terms.
            </p>

            <div className="grid gap-6 sm:grid-cols-2">
              {perks.map((perk, index) => (
                <motion.div
                  key={index}
                  className="border-t border-[#ff5f47]/18 pt-5"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#0A5C36] text-white">
                    {perk.icon}
                  </div>
                  <h3 className="font-asgard text-2xl leading-tight text-[#02120A]">{perk.title}</h3>
                  <p className="mt-2 font-satoshi text-sm leading-6 text-[#02120A]/70 sm:text-base">{perk.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="relative order-1 lg:order-2"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="relative z-10 overflow-hidden rounded-[34px]">
              <div className="relative aspect-[4/5] overflow-hidden rounded-[28px]">
                <Image
                  src="/early-access-perks/3.png"
                  alt="Cribnosh Food Creator"
                  fill
                  className="object-cover"
                  sizes="(min-width: 1024px) 38vw, 100vw"
                />
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-[#02120A]/28 via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
